import { last } from 'lodash-es';

import makeChartConfig from '../../../../../components/Chart/LineChart';
import { numberToHumanChart } from '../../../../../utils/formatter';
import { getFallbackSubDateByGranularity } from '../../../../../utils/helpers';
import { tooltipRowTemplate } from '../../../../../utils/charts/chart-tooltips';
import {
  format,
  formatQuarterYear,
  today,
  yesterday,
  differenceInDays,
} from '../../../../../utils/date';

const formatByGranularity = (date, granularity) => {
  if (granularity === 'weekly') return format(date, 'MM/DD/YY');
  if (granularity === 'monthly') return format(date, 'MM/YY');
  return formatQuarterYear(date);
};

export const formatToDatePeriod = (date, toDatePeriod) => {
  if (toDatePeriod === 'year') return format(date, 'YYYY');
  return formatQuarterYear(date, { yearFormat: 'YYYY' });
};

const postfixByMetric = {
  total_time_spent: 'hrs',
};

export const chartYearConfig = (
  seriesByMetric,
  {
    granularity,
    overPeriod,
    rotateLabels,
    height,
    animation = {
      duration: 300,
    },
    yGridLine,
    variant,
    maxDate,
    subDates,
  }
) => {
  const startPartialDate = 1;
  const endPartialDate =
    seriesByMetric.length > 0
      ? differenceInDays(maxDate || yesterday(), last(seriesByMetric)[0]) + 1
      : startPartialDate;

  const xTickPositions = seriesByMetric.map((value) => value[0]);

  const spacingByVariant = {
    qv: [10, 0, 0, 0],
    v1: [10, -24, 24, 0],
    v2: [10, 0, 22, 0],
  };

  const xAxisLabelOffsetByVariant = {
    qv: 20,
    v1: 46,
    v2: 34,
  };

  return makeChartConfig({
    chartOptions: {
      type: 'spline',
      spacing: spacingByVariant[variant],
      height,
      animation,
    },
    yAxis: {
      title: null,
      tickAmount: 6,
      startOnTick: true,
      labels: {
        reserveSpace: true,
        y: 5,
        style: {
          color: '#848EA1',
          fontSize: 12,
        },
        formatter() {
          return `${this.value}%`;
        },
      },
      ...(yGridLine
        ? {
            gridLineWidth: 1,
            gridLineColor: '#EDF0F5',
          }
        : {}),
    },
    xAxisOptions: {
      tickLength: 0,
      lineWidth: 0,
      crosshair: false,
      minPadding: 0,
      tickPositions: xTickPositions.length > 0 ? xTickPositions : null,
    },
    xAxisLabels: {
      formatter() {
        return `<span>${formatByGranularity(this.value, granularity)}</span>`;
      },
      style: {
        color: '#848EA1',
        fontSize: 12,
      },
      showFirstLabel: true,
      autoRotation: true,
      y: xAxisLabelOffsetByVariant[variant],
      ...(rotateLabels ? { rotation: -45 } : {}),
    },
    tooltipOptions: {
      borderWidth: 0,
      padding: 0,
      visible: true,
      shared: true,
      valueDecimals: 2,
      useHTML: true,
      outside: true,
      className: 'quick-view-tooltip',
      formatter() {
        const isPartialElem = this.points[0].point.options.isPartial;

        const currentDate = formatByGranularity(this.x, granularity);
        let subDate;

        if (subDates?.[this.x]) {
          subDate = subDates[this.x];
        } else {
          // Fallback
          subDate = getFallbackSubDateByGranularity({
            isOverPeriod: overPeriod === 'period-over-period',
            granularity,
            date: this.x,
          });
        }
        const prevDate = formatByGranularity(subDate, granularity);

        return `
          <div
            style="
              min-width: 170px;
              border-radius: 8px;
              border: 1px solid #E1E5EB;
              padding: 12px 16px;
              background: #fff;
              box-shadow: 0px 0px 24px 0px rgba(0, 0, 0, 0.08);
            "
          >
            <table class="table">
              <tr style=display:${!isPartialElem && 'none'}>
                <td class="text-left text-black text-12 p-l-0 p-b-0 pr-md">
                  Days ${
                    endPartialDate === 0
                      ? `${startPartialDate}`
                      : `${startPartialDate} - ${endPartialDate}`
                  }
                </td>
              </tr>
              <tr>
                <td class="text-left text-black text-12 p-l-0 p-b-0 pr-md">
                  ${currentDate} vs ${prevDate}
                </td>
                <td class="text-right  text-12 p-l-0 p-b-0" style="color: #5D6B85, line-height:150%">
                  <div class="text-bold text-black text-16">
                    <span>${this.y}%</span>
                  </div>
                </td>
              </tr>
            </table>
          </div>
        `;
      },
    },
    plotOptionsSeries: {
      animation,
    },
    plotOptionsSettings: {
      column: {
        minPointLength: 5,
        groupPadding: 0.05,
        borderRadius: 0,
      },
    },
  });
};

export const chartQuarterConfig = ({
  metric,
  toDatePeriod,
  labelStep,
  height,
  animation = {
    duration: 300,
  },
  floor,
  ceiling,
  variant,
  maxDate,
}) => {
  const tickAmountByVariant = {
    qv: undefined,
    v1: 5,
    v2: 5,
  };

  const currPeriod = formatToDatePeriod(maxDate || today(), toDatePeriod);
  let prevPeriod;

  if (toDatePeriod === 'year') {
    prevPeriod = `${currPeriod - 1}`;
  } else {
    const [quarter, year] = currPeriod.split('Q');
    prevPeriod = `${quarter}Q${year - 1}`;
  }

  return makeChartConfig({
    chartOptions: {
      type: 'spline',
      spacing: [10, 6, 0, 6],
      height,
      animation,
    },
    yAxis: {
      title: null,
      floor,
      ceiling,
      minPadding: 0.01,
      maxPadding: 0.01,
      startOnTick: true,
      tickAmount: tickAmountByVariant[variant],
      labels: {
        reserveSpace: true,
        style: {
          color: '#848EA1',
          fontSize: 12,
        },
        formatter() {
          return numberToHumanChart(this.value);
        },
      },
    },
    xAxisOptions: {
      crosshair: false,
      lineWidth: 0,
      tickLength: 0,
      type: 'category',
    },
    xAxisLabels: {
      formatter() {
        return `<span>${this.isFirst ? 'Day ' : ''}${this.value}</span>`;
      },
      style: {
        color: '#848EA1',
        fontSize: 12,
      },
      step: labelStep,
      y: 30,
    },
    tooltipOptions: {
      borderWidth: 0,
      padding: 0,
      shared: true,
      valueDecimals: 2,
      useHTML: true,
      outside: true,
      className: 'quick-view-tooltip',
      formatter() {
        const date = `Day ${this.x}`;
        let rows;

        if (Array.isArray(this.points)) {
          let { points } = this;

          if (points.length === 1) {
            const pointDate = formatToDatePeriod(points[0].point?.date, toDatePeriod);

            if (pointDate === prevPeriod) {
              points = [...this.points, { isStub: true, color: '#15CFD4', date: currPeriod }];
            } else if (pointDate === currPeriod) {
              points = [{ isStub: true, color: '#E1E5EB', date: prevPeriod }, ...this.points];
            }
          }

          rows = points.map((point) => {
            const formattedPoint = {
              color: point.series.color || point.color,
              name: point.isStub ? point.date : formatToDatePeriod(point.point.date, toDatePeriod),
              y: `${point.isStub ? '—' : numberToHumanChart(point.y)} ${
                postfixByMetric[metric] || ''
              }`,
            };
            return tooltipRowTemplate(formattedPoint);
          });
        } else return false;

        return `
          <div
            style="
              min-width: 189px;
              border-radius: 8px;
              border: 1px solid #E1E5EB;
              padding: 16px;
              background: #fff;
              box-shadow: 0px 0px 24px 0px rgba(0, 0, 0, 0.08);
            "
          >
            <div style="
            font-size: 12px;
            font-style: normal;
            font-weight: 400;
            line-height: 150%;
            color: #848EA1;
            margin-bottom: 8px"
            >
            ${date}
            </div>
            <table class="table">${rows.join('')}</table>
          </div>
        `;
      },
    },
    plotOptionsSeries: {
      animation,
    },
  });
};
